// src/pages/Setting.jsx

import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import left from "../../assets/icons/Arrow/left.svg";
import { HeaderIconL } from "../../assets/components/HeaderGroup";
import { useFixedLayout } from "../../hooks/useFixedLayout";
import { TwinColumn } from "../../assets/components/ButtonGroup"
import Popup from "../../assets/components/PopupGroup";
import { useToast } from "../../hooks/useToast";
import { sendToApp } from "../../bridges/appBridge";
import right from "../../assets/icons/Chevron/right_black.svg"

const API = process.env.REACT_APP_FASTAPI_ADS_URL;

// 재사용 가능한 토글 컴포넌트
function Toggle({ checked, onChange }) {
    return (
        <button
            type="button"
            onClick={onChange}
            className={`w-[52px] h-[32px] rounded-full flex items-center px-[3px] transition-all
            ${checked ? "bg-btn-primary-fill" : "bg-btn-disabled-fill"}`}
        >
            <span
                className={`
          w-[24px] h-[24px] rounded-full bg-surface-light-white-absolute transition-transform
          ${checked ? "translate-x-[20px]" : "translate-x-0"}
        `}
            />
        </button>
    );
}

// 날짜 포맷 (2025년 10월 30일)
function formatKoreanDate(date) {
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    return `${y}년 ${m}월 ${d}일`;
}

const Setting = () => {
    const navigate = useNavigate();
    const { headerRef, headerSpace } = useFixedLayout();
    const { showToast } = useToast();
    const location = useLocation();
    const userId = location.state.userId;

    // 페이지 진입 시 설정 조회
    const [marketingOptIn, setMarketingOptIn] = useState(null);
    const [autoLogin, setAutoLogin] = useState(null);

    // 거부 팝업 상태
    const [rejectModalOpen, setRejectModalOpen] = useState(false);

    useEffect(() => {
        const fetchMarketingOpt = async () => {
            if (!userId) return;
            try {
                const res = await axios.get(`${API}/push/get/marketing`, {
                    params: { user_id: userId },
                });
                const { marketing_opt, auto_login_status } = res.data || {};
                setMarketingOptIn(marketing_opt === 1);
                setAutoLogin(auto_login_status === "active");
            } catch (e) {
                console.error(e);
                setMarketingOptIn(false);
                setAutoLogin(false);
            }
        };
        fetchMarketingOpt();
    }, [userId]);

    const handleHome = () => {
        navigate("/ads/main", { state: { tab: "mypage" } });
    };

    // 마케팅 토글 클릭 핸들러
    const handleToggleMarketing = async () => {
        if (!marketingOptIn) {
            // OFF → ON : 바로 켜고 토스트 + 서버 반영
            setMarketingOptIn(true);

            try {
                await axios.post(`${API}/push/update/marketing`, {
                    opt: 1,
                    user_id: userId,
                });
            } catch (e) {
                // 실패 시 롤백
                setMarketingOptIn(false);
                console.error(e);
                showToast(
                    `마케팅 정보 수신 동의 변경을 실패했습니다.`,
                    { placement: "bottom", bottomOffset: "8px" }
                );
            }

            const today = new Date();
            showToast(
                `마케팅 정보 수신에 동의했습니다 (${formatKoreanDate(today)})`,
                { placement: "bottom", bottomOffset: "8px" }
            );
        } else {
            // ON → OFF : 바로 끄지 않고 팝업 먼저
            setRejectModalOpen(true);
        }
    };

    // 팝업에서 "수신 거부" 선택
    const handleConfirmReject = async () => {
        // UI 먼저 변경
        setMarketingOptIn(false);
        setRejectModalOpen(false);

        try {
            await axios.post(`${API}/push/update/marketing`, {
                opt: 0,
                user_id: userId,
            });
        } catch (e) {
            // 실패 시 롤백
            setMarketingOptIn(true);
            console.error(e);
            showToast(
                `마케팅 정보 수신 거부 변경을 실패했습니다.`,
                { placement: "bottom", bottomOffset: "8px" }
            );
        }

        const today = new Date();
        showToast(
            `마케팅 정보 수신을 거부했습니다 (${formatKoreanDate(today)})`,
            { placement: "bottom", bottomOffset: "8px" }
        );
    };



    // 팝업에서 "취소" 선택
    const handleCancelReject = () => {
        setRejectModalOpen(false);
    };

    const handleToggleAutoLogin = async () => {
        const newValue = !autoLogin;
        setAutoLogin(newValue);

        try {
            await axios.post(`${API}/ads/auto/login/update`, {
                user_id: userId,
                auto_login: newValue ? 1 : 0,
            });

            // 자동 로그인 ON일 땐 토스트 안 띄움
            if (!newValue) {
                showToast(
                    "자동 로그인 설정을 해제했습니다",
                    { placement: "bottom", bottomOffset: "8px" }
                );
            }
        } catch (e) {
            console.error(e);
            setAutoLogin(!newValue); // 롤백

            showToast(
                "자동 로그인 설정 변경에 실패했습니다.",
                { placement: "bottom", bottomOffset: "8px" }
            );
        }
    };

    // ✅ 테스트용: 앱 알림 설정 화면 열기
    const handleOpenNotificationSettings = () => {
        try {
            // RN 쪽으로 "OPEN_NOTIFICATION_SETTINGS" 메시지 전송
            sendToApp("OPEN_NOTIFICATION_SETTINGS");
        } catch (e) {
            console.error(e);
            showToast("앱 환경설정을 열 수 없습니다", {
                placement: "bottom",
                bottomOffset: "8px",
            });
        }
    };


    return (
        <div className="flex flex-col h-dvh bg-surface-default">
            {/* 상단 헤더 */}
            <div ref={headerRef} className="fixed top-0 left-0 right-0 z-10">
                <HeaderIconL
                    onIconClick={handleHome}
                    icon={left}
                    title="환경 설정"
                />
            </div>

            {/* 내용 영역 */}
            <div
                className=" flex-1 overflow-y-auto space-y-[24px]"
                style={{ paddingTop: (headerSpace ?? 0) + 16 }}
            >
                {/* 마케팅 정보 수신 설정 */}
                <div className="flex items-center justify-between p-[16px] pb-0">
                    <span className="text-b1 text-text-default">마케팅 정보 수신 설정</span>
                    {marketingOptIn !== null && (
                        <Toggle checked={marketingOptIn} onChange={handleToggleMarketing} />
                    )}
                </div>

                {/* 자동 로그인 설정 */}
                <div className="flex items-center justify-between px-[16px]">
                    <span className="text-b1 text-text-default">자동 로그인 설정</span>
                    {autoLogin !== null && (
                        <Toggle checked={autoLogin} onChange={handleToggleAutoLogin} />
                    )}
                </div>

                <div
                    className="bg-surface-light-subtle h-[8px]"
                />

                {/* 🔧 테스트용: 안드로이드 앱 알림 설정 화면 열기 */}
                <div className="flex items-center justify-between cursor-pointer px-[16px]">
                    <span className="text-b1 text-text-default ">
                        시스템 알림 수신 설정
                    </span>
                    <img
                        className=""
                        src={right}
                        onClick={handleOpenNotificationSettings}
                        alt="이동"
                    />
                </div>
            </div>

            {/* 마케팅 수신 거부 팝업 */}
            <Popup
                isOpen={rejectModalOpen}
                onClose={handleCancelReject}
                title="마케팅 정보 수신 거부"
                subtitle={
                    `할인 혜택, 이벤트 소식, 맞춤 추천 등 유용한 정보를 안내받지 못할 수 있어요.
                    \n 그래도 거부하시겠습니까?`
                }

                renderButtons={
                    <TwinColumn
                        topType="fill"
                        topText="수신 거부"
                        bottomType="border"
                        bottomText="취소"
                        onTop={handleConfirmReject}
                        onBottom={handleCancelReject}
                    />
                }
            />
        </div>
    );
};

export default Setting;
